import { useMemo, useState } from "react"
import { format, parseISO } from "date-fns"
import {
	IconCalendarOutline18 as CalendarIcon,
	IconCalendarXmarkOutline18 as CalendarXmarkIcon,
	IconArrowDashedRotateClockwiseOutline18 as CycleIcon,
	IconGauge2Outline18 as EstimateIcon,
	IconTagOutline18 as LabelsIcon,
	IconDotsOutline18 as MoreIcon,
	IconArrowCornerUpLeftOutline18 as ParentIcon,
	IconUserOutline18 as UserIcon,
	IconUserXmarkOutline18 as UserXmarkIcon,
} from "nucleo-ui-outline-18"

import type { IconType } from "@productlane/ui"
import type { Issue, LinearCycle, LinearUser } from "@productlane/zero"
import { cycleLabel } from "@productlane/lib/src/linear-cycle"
import {
	Button,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	LinearIcon,
	Popover,
} from "@productlane/ui"
import { Calendar } from "@productlane/ui/src/components/internal/calendar"

import type { OptionalProperty } from "./optional-properties"
import type { RouterInputs, RouterOutputs } from "@/lib/api"
import { LabelSelect } from "@/components/label-select"
import { PopoverButton } from "@/components/popover-button"
import {
	propertyIconClassName,
	PropertyTrigger,
} from "@/components/property-trigger"
import { toast } from "@/components/toast"
import { toggleLabelId } from "@/components/toggle-label-id"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { useRole } from "@/lib/useRole"
import {
	AGENT_MENU_VALUE,
	agentValue,
	assigneeAndAgentOptions,
	findSelf,
	OwnerAvatars,
	parseAssigneeChoice,
} from "./assignee-options"
import { IssueParentSelect } from "./issue-parent-select"
import { splitOptionalProperties } from "./optional-properties"
import { useSaveIssueToLinear } from "./use-save-issue-to-linear"

type Properties = RouterOutputs["linear"]["issueProperties"]
type PropertiesUpdate = Omit<
	RouterInputs["linear"]["updateIssueProperties"],
	"issueId"
>

export type IssueWithLinearRelations = Issue & {
	assignee?: LinearUser | undefined
	delegate?: LinearUser | undefined
	cycle?: LinearCycle | undefined
}

const OPTION_QUERY = { cacheTime: Infinity, staleTime: Infinity } as const

const OPTIONAL_PROPERTY_MENU: Record<
	OptionalProperty,
	{ label: string; icon: IconType }
> = {
	labels: { label: "Labels", icon: LabelsIcon },
	cycle: { label: "Cycle", icon: CycleIcon },
	estimate: { label: "Estimate", icon: EstimateIcon },
	dueDate: { label: "Due date", icon: CalendarIcon },
	parent: { label: "Parent issue", icon: ParentIcon },
}

interface Person {
	id: string
	name: string
	avatarUrl?: string | null
}

// The Zero row can trail an optimistic pick by a moment, so the picker's own
// list fills in until it arrives.
const resolvePerson = (
	id: string | null | undefined,
	row: LinearUser | undefined,
	picked: readonly Person[] | null | undefined,
): Person | null => {
	if (!id) return null
	if (row) return { id, name: row.name ?? "", avatarUrl: row.avatarUrl }
	return picked?.find((person) => person.id === id) ?? null
}

export const IssueLinearProperties = ({
	issue,
}: {
	issue: IssueWithLinearRelations
}) => {
	const { role } = useRole()
	const { session } = useSession()
	const isViewer = role === "VIEWER"
	const utils = api.useUtils()
	const queryInput = { issueId: issue.id }
	const saveToLinear = useSaveIssueToLinear(issue.id)

	const { data: properties } = api.linear.issueProperties.useQuery(queryInput, {
		retry: false,
		staleTime: 30_000,
	})

	const [isAssigneeOpen, setIsAssigneeOpen] = useState(false)
	const [isLabelsOpen, setIsLabelsOpen] = useState(false)
	const [isCycleOpen, setIsCycleOpen] = useState(false)
	const [isDueDateOpen, setIsDueDateOpen] = useState(false)
	const [isEstimateOpen, setIsEstimateOpen] = useState(false)
	const [isParentOpen, setIsParentOpen] = useState(false)
	const [revealed, setRevealed] = useState<ReadonlySet<OptionalProperty>>(
		() => new Set(),
	)

	const teamId = properties?.teamId ?? issue.team
	const { data: members } = api.linear.members.useQuery(
		{ teamId },
		{
			...OPTION_QUERY,
			enabled: isAssigneeOpen || (!!issue.assigneeId && !issue.assignee),
		},
	)
	const { data: agents } = api.linear.agents.useQuery(undefined, {
		...OPTION_QUERY,
		enabled: isAssigneeOpen || (!!issue.delegateId && !issue.delegate),
	})
	const { data: allLabels } = api.linear.labels.useQuery(undefined, {
		...OPTION_QUERY,
		enabled: isLabelsOpen,
	})
	const { data: cycles } = api.linear.teamCycles.useQuery(
		{ teamId },
		{
			...OPTION_QUERY,
			enabled: isCycleOpen || (!!issue.cycleId && !issue.cycle),
		},
	)

	const mutation = api.linear.updateIssueProperties.useMutation()

	const save = async (
		update: PropertiesUpdate,
		optimistic: Partial<Properties>,
		event: string,
	) => {
		const previous = utils.linear.issueProperties.getData(queryInput)
		if (previous) {
			utils.linear.issueProperties.setData(queryInput, {
				...previous,
				...optimistic,
			})
		}
		try {
			await mutation.mutateAsync({ issueId: issue.id, ...update })
			analytics.track(event)
		} catch (error) {
			if (previous) utils.linear.issueProperties.setData(queryInput, previous)
			toast.error({
				title: "Linear did not save this change",
				description: error instanceof Error ? error.message : undefined,
				icon: LinearIcon,
			})
		} finally {
			void utils.linear.issueProperties.invalidate(queryInput)
		}
	}

	const assigneeOptions = useMemo(
		() =>
			assigneeAndAgentOptions({
				members,
				agents,
				selfEmail: session?.email,
				assigneeId: issue.assigneeId,
				delegateId: issue.delegateId,
			}),
		[members, agents, session?.email, issue.assigneeId, issue.delegateId],
	)

	const cycleOptions = useMemo(
		() =>
			(cycles ?? []).map((cycle) => ({
				value: cycle.id,
				label: (
					<div className="flex items-center gap-2">
						<span className="truncate">{cycle.label}</span>
						<span className="flex-none text-tertiary">
							{format(parseISO(cycle.startsAt), "MMM d")} to{" "}
							{format(parseISO(cycle.endsAt), "MMM d")}
						</span>
					</div>
				),
			})),
		[cycles],
	)

	const estimateOptions = useMemo(
		() =>
			(properties?.estimateOptions ?? []).map((option) => ({
				value: String(option.value),
				label: option.label,
			})),
		[properties?.estimateOptions],
	)

	const assignee = resolvePerson(issue.assigneeId, issue.assignee, members)
	const delegate = resolvePerson(issue.delegateId, issue.delegate, agents)
	const cycleName = issue.cycle
		? cycleLabel(issue.cycle)
		: (cycles?.find(({ id }) => id === issue.cycleId)?.label ?? null)

	// The chip names the person responsible; an agent only stands in when nobody is.
	const owner = assignee ?? delegate
	const assigneeTrigger = (
		<PropertyTrigger
			tooltip="Change assignee"
			icon={
				owner ? (
					<OwnerAvatars
						assignee={owner}
						delegate={assignee ? delegate : null}
					/>
				) : (
					<UserIcon className={propertyIconClassName} />
				)
			}
		>
			{owner?.name ?? "Assignee"}
		</PropertyTrigger>
	)

	const cycleTrigger = (
		<PropertyTrigger
			tooltip="Change cycle"
			icon={<CycleIcon className={propertyIconClassName} />}
		>
			{issue.cycleId ? (cycleName ?? "Cycle") : "Cycle"}
		</PropertyTrigger>
	)

	const estimateTrigger = properties && (
		<PropertyTrigger
			tooltip="Change estimate"
			icon={<EstimateIcon className={propertyIconClassName} />}
		>
			{properties.estimateLabel ?? "Estimate"}
		</PropertyTrigger>
	)

	const dueDate = properties?.dueDate ? parseISO(properties.dueDate) : undefined
	const dueDateTrigger = (
		<PropertyTrigger
			tooltip="Change due date"
			icon={<CalendarIcon className={propertyIconClassName} />}
		>
			{dueDate ? format(dueDate, "MMM d, yyyy") : "Due date"}
		</PropertyTrigger>
	)

	const selectedLabelIds = properties?.labels.map((label) => label.id) ?? []
	const { shown, hidden } = splitOptionalProperties({
		available: {
			labels: !!properties,
			cycle: !!properties?.cyclesEnabled,
			estimate: estimateOptions.length > 0,
			dueDate: !!properties,
			parent: !isViewer,
		},
		isSet: {
			labels: selectedLabelIds.length > 0,
			cycle: !!issue.cycleId,
			estimate: estimateOptions.length > 0 && properties?.estimate != null,
			dueDate: !!dueDate,
			parent: !!issue.parentLinearId,
		},
		revealed,
	})
	const isShown = (property: OptionalProperty) => shown.includes(property)

	const setOpen: Record<OptionalProperty, (open: boolean) => void> = {
		labels: setIsLabelsOpen,
		cycle: setIsCycleOpen,
		estimate: setIsEstimateOpen,
		dueDate: setIsDueDateOpen,
		parent: setIsParentOpen,
	}
	const reveal = (property: OptionalProperty) => {
		setRevealed((current) => new Set(current).add(property))
		// Opens once the menu has closed, so its dismissal can't close the picker too.
		setTimeout(() => setOpen[property](true), 0)
	}

	const parentSelect = (
		<IssueParentSelect
			issue={issue}
			open={isParentOpen}
			onOpenChange={setIsParentOpen}
		/>
	)

	if (isViewer) {
		return (
			<>
				{assigneeTrigger}
				{isShown("cycle") && cycleTrigger}
				{isShown("estimate") && estimateTrigger}
				{isShown("dueDate") && dueDateTrigger}
				{isShown("parent") && parentSelect}
			</>
		)
	}

	return (
		<>
			<PopoverButton
				data={assigneeOptions}
				selected={
					issue.assigneeId ?? (issue.delegateId ? AGENT_MENU_VALUE : undefined)
				}
				selectedItems={[
					...(issue.assigneeId ? [issue.assigneeId] : []),
					...(issue.delegateId ? [agentValue(issue.delegateId)] : []),
				]}
				isLoading={isAssigneeOpen && (!members || !agents)}
				onOpenChange={setIsAssigneeOpen}
				placeholder="Assign to..."
				resetOption="No assignee"
				resetIcon={UserXmarkIcon}
				numberShortcuts
				onChange={(value) => {
					const choice = parseAssigneeChoice(value, {
						assigneeId: issue.assigneeId,
						selfId: findSelf(members, session?.email)?.id,
					})
					void saveToLinear(
						choice,
						choice.delegateId !== undefined
							? "issue-delegate-update"
							: "issue-assignee-update",
					)
				}}
			>
				{assigneeTrigger}
			</PopoverButton>

			{properties && isShown("labels") && (
				<LabelSelect
					asProperty
					labels={allLabels ?? properties.labels}
					selectedLabelIds={selectedLabelIds}
					teamId={teamId}
					variant="ghost"
					open={isLabelsOpen}
					onOpenChange={setIsLabelsOpen}
					onChange={(labelId) => {
						const labels = allLabels ?? properties.labels
						const labelIds = toggleLabelId(labels, selectedLabelIds, labelId)
						void save(
							{ labelIds },
							{ labels: labels.filter(({ id }) => labelIds.includes(id)) },
							"issue-labels-update",
						)
					}}
				/>
			)}

			{isShown("cycle") && (
				<PopoverButton
					data={cycleOptions}
					selected={issue.cycleId ?? undefined}
					isLoading={isCycleOpen && !cycles}
					open={isCycleOpen}
					onOpenChange={setIsCycleOpen}
					placeholder="Move to cycle..."
					resetOption="No cycle"
					resetIcon={CycleIcon}
					onChange={(cycleId) =>
						void saveToLinear({ cycleId }, "issue-cycle-update")
					}
				>
					{cycleTrigger}
				</PopoverButton>
			)}

			{properties && isShown("estimate") && (
				<PopoverButton
					data={estimateOptions}
					selected={
						properties.estimate === null
							? undefined
							: String(properties.estimate)
					}
					open={isEstimateOpen}
					onOpenChange={setIsEstimateOpen}
					placeholder="Set estimate..."
					resetOption="No estimate"
					resetIcon={EstimateIcon}
					onChange={(value) => {
						const estimate = value === null ? null : Number(value)
						void save(
							{ estimate },
							{
								estimate,
								estimateLabel:
									estimateOptions.find((option) => option.value === value)
										?.label ?? null,
							},
							"issue-estimate-update",
						)
					}}
				>
					{estimateTrigger}
				</PopoverButton>
			)}

			{properties && isShown("dueDate") && (
				<Popover
					open={isDueDateOpen}
					onOpenChange={setIsDueDateOpen}
					className="w-auto p-0"
					content={
						<div className="flex flex-col">
							<Calendar
								mode="single"
								selected={dueDate}
								defaultMonth={dueDate}
								onSelect={(date) => {
									setIsDueDateOpen(false)
									if (!date) return
									const value = format(date, "yyyy-MM-dd")
									void save(
										{ dueDate: value },
										{ dueDate: value },
										"issue-due-date-update",
									)
								}}
								autoFocus
							/>
							{dueDate && (
								<div className="border-t border-gray-200 p-2 dark:border-gray-700/80">
									<Button
										variant="ghost"
										className="w-full justify-start"
										Icon={CalendarXmarkIcon}
										onClick={() => {
											setIsDueDateOpen(false)
											void save(
												{ dueDate: null },
												{ dueDate: null },
												"issue-due-date-update",
											)
										}}
									>
										Remove due date
									</Button>
								</div>
							)}
						</div>
					}
				>
					{dueDateTrigger}
				</Popover>
			)}

			{isShown("parent") && parentSelect}

			{hidden.length > 0 && (
				<DropdownMenu
					trigger={
						<IconButton
							variant="ghost"
							Icon={MoreIcon}
							aria-label="More options"
						/>
					}
					contentProps={{
						align: "start",
						onCloseAutoFocus: (event) => event.preventDefault(),
					}}
				>
					{hidden.map((property) => (
						<DropdownMenuItem
							key={property}
							Icon={OPTIONAL_PROPERTY_MENU[property].icon}
							onSelect={() => reveal(property)}
						>
							{OPTIONAL_PROPERTY_MENU[property].label}
						</DropdownMenuItem>
					))}
				</DropdownMenu>
			)}
		</>
	)
}
